import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "Loyalty Program Design and Implementation",
  seoDescription: "Loyalty programs that change customer behavior, not just reward it. Build tier systems, rewards, and engagement that drive measurable LTV growth.",
  canonicalUrl: "https://xperfomax.com/loyalty-programs",
  category: "services",
  categoryLabel: "Services / Loyalty Programs",
  headline: "Loyalty Program Design and Implementation",
  subheadline: "Loyalty programs that change customer behavior, not just reward it. Build tier systems, rewards, and engagement that drive measurable LTV growth.",
  ctaText: "Start a growth conversation",
  problemTitle: "Why Loyalty Programs Fail",
  problems: [
    "Points programs reward existing behavior without changing it.",
    "Reward economics are unsustainable or unclear.",
    "Members enroll but never engage beyond sign-up.",
    "No visibility into incremental revenue from loyalty.",
  ],
  systemTitle: "Our Loyalty System",
  systemDescription: "Behavior-changing mechanics, sustainable economics, and engagement architecture built for measurable LTV lift.",
  services: [
    { name: "Program Strategy", icon: "Target", components: ["Reward structure design", "Tier architecture", "Economic modeling"], outcome: "A loyalty program that actually changes customer behavior." },
    { name: "Points & Rewards Engine", icon: "Coins", components: ["Earn and burn mechanics", "Reward catalog development", "Redemption experience design"], outcome: "Flexible reward systems that drive the right actions." },
    { name: "Tier & Status Systems", icon: "Trophy", components: ["Tier threshold optimization", "Status benefit differentiation", "Progression mechanics"], outcome: "Tiered experiences that motivate continued engagement." },
    { name: "Member Engagement", icon: "Heart", components: ["Personalized communications", "Milestone celebrations", "Exclusive access campaigns"], outcome: "Members who feel valued and engaged, not just enrolled." },
    { name: "Referral Integration", icon: "Users", components: ["Advocate identification", "Referral incentive design", "Viral loop optimization"], outcome: "Turn best customers into acquisition channels." },
    { name: "Loyalty Analytics", icon: "BarChart3", components: ["Member behavior tracking", "Program ROI measurement", "Incremental revenue attribution"], outcome: "Clear visibility into loyalty-driven revenue lift." },
  ],
  processTitle: "From design to optimization.",
  processSteps: [
    { number: "01", title: "Program Audit & Design", description: "Analyze current loyalty efforts, benchmark competitors, and design a program architecture aligned with business goals." },
    { number: "02", title: "Economic Modeling", description: "Build financial models for reward costs, breakage rates, and incremental revenue to ensure program profitability." },
    { number: "03", title: "Platform Selection", description: "Evaluate and implement loyalty technology that integrates with existing systems and scales with growth." },
    { number: "04", title: "Launch & Enrollment", description: "Deploy the program with enrollment campaigns, member onboarding, and initial reward activation." },
    { number: "05", title: "Optimization & Growth", description: "Continuously optimize based on member behavior data, engagement metrics, and incremental revenue analysis." },
  ],
  timelinePhases: [
    { number: "01", title: "Program Audit & Design", description: "Analyze current efforts and design program architecture.", decision: "What behaviors the program should incentivize.", output: "Program design document with tier and reward structures.", riskRemoved: "Copying competitor programs without strategic fit." },
    { number: "02", title: "Economic Modeling", description: "Build financial models for program profitability.", decision: "Reward values that balance motivation and margin.", output: "Financial projections with breakeven analysis.", riskRemoved: "Unsustainable reward economics." },
    { number: "03", title: "Platform Selection", description: "Evaluate and implement loyalty technology.", decision: "Build vs buy and platform requirements.", output: "Integrated loyalty platform with data connections.", riskRemoved: "Technology that limits program flexibility." },
    { number: "04", title: "Launch & Enrollment", description: "Deploy with enrollment and onboarding campaigns.", decision: "How to drive initial member acquisition.", output: "Launched program with active member base.", riskRemoved: "Low initial adoption killing momentum." },
    { number: "05", title: "Optimization & Growth", description: "Continuously optimize based on behavior data.", decision: "What to adjust for higher engagement.", output: "Evolved program with proven revenue impact.", riskRemoved: "Stagnant programs that lose relevance." },
  ],
  technologyCategories: [
    { title: "Loyalty Platforms", icon: "Gift", items: ["Yotpo", "Smile.io", "LoyaltyLion", "Annex Cloud"] },
    { title: "Customer Data", icon: "Database", items: ["Segment", "Klaviyo", "HubSpot"] },
    { title: "Analytics", icon: "LineChart", items: ["Mixpanel", "GA4", "Looker Studio"] },
    { title: "Rewards", icon: "Star", items: ["Tremendous", "Tango Card", "Custom Catalogs"] },
  ],
  techTitle: "Loyalty Program Tools",
  techDescription: "Enterprise-grade loyalty platforms and integrations.",
  qualification: {
    forYou: [
      "You have repeat purchase potential in your business model",
      "You can invest in proper program infrastructure",
      "You want to increase LTV and reduce churn systematically",
      "You are ready to commit to long-term member engagement",
    ],
    notForYou: [
      "You have a one-time purchase business model",
      "You want a simple discount program",
      "You are not ready to invest in loyalty technology",
      "You expect immediate ROI without building member base",
    ],
  },
  faqs: [
    { question: "How long does it take to launch a loyalty program?", answer: "A basic points program can launch in 8 to 12 weeks. Full-featured programs with tiers, referrals, and custom rewards typically take 3 to 4 months to design, build, and deploy properly." },
    { question: "What makes a loyalty program successful?", answer: "Successful programs change behavior, not just reward existing behavior. The key is designing rewards and tiers that motivate incremental actions while maintaining sustainable economics." },
    { question: "How do you measure loyalty program ROI?", answer: "We measure incremental revenue from members versus non-members, controlling for selection bias. Key metrics include member LTV lift, purchase frequency increase, and program-attributed revenue." },
    { question: "Should we build or buy a loyalty platform?", answer: "Most brands should buy. Modern loyalty platforms offer flexibility and integration capabilities that make custom builds unnecessary. We recommend building custom only for very specific requirements." },
  ],
  finalCtaTitle: "Build a loyalty program that drives real growth.",
  finalCtaDescription: "Most loyalty programs are cost centers. Ours become growth engines that compound customer lifetime value.",
  finalCtaButtonText: "Start a growth conversation",
};

const LoyaltyPrograms = () => {
  const { content } = usePageContent("loyalty-programs", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      timelinePhases={content.timelinePhases}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default LoyaltyPrograms;
